import { useState, useEffect, useRef, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.jsx'
import styles from './LakePage.module.css'

// ── UI constants (not data) ────────────────────────────────────────────────────

const SCHEDULE_OPTIONS = [
  { id: 'hourly',          label: 'Hourly',             cron: '0 * * * *',    hours: 1   },
  { id: 'every_4h',        label: 'Every 4 hours',      cron: '0 */4 * * *',  hours: 4   },
  { id: 'every_6h',        label: 'Every 6 hours',      cron: '0 */6 * * *',  hours: 6   },
  { id: 'every_12h',       label: 'Every 12 hours',     cron: '0 */12 * * *', hours: 12  },
  { id: 'daily_6am',       label: 'Daily at 6:00 AM',   cron: '0 6 * * *',    hours: 24  },
  { id: 'daily_midnight',  label: 'Daily at midnight',  cron: '0 0 * * *',    hours: 24  },
  { id: 'weekly',          label: 'Weekly (Monday)',     cron: '0 6 * * 1',    hours: 168 },
  { id: 'off',             label: 'Off — manual only',  cron: null,            hours: null },
]

const DBT_TAGS = ['prepared', 'analytics']

// Schema that drives the dynamic column editor — add a new entry here to expose a new editable field
const COLUMN_FIELD_SCHEMA = [
  {
    key: 'description',
    label: 'Description',
    type: 'text',
    placeholder: 'Business-friendly description',
    required: true,
  },
  {
    key: 'sensitivity',
    label: 'Sensitivity',
    type: 'select',
    options: [
      { value: '',       label: 'None' },
      { value: 'medium', label: 'Medium — contact info (email, phone, IP)' },
      { value: 'high',   label: 'High — PII (SSN, DOB, financial IDs)' },
    ],
  },
  {
    key: 'classification',
    label: 'Classification',
    type: 'select',
    options: [
      { value: '',             label: 'Unset' },
      { value: 'public',       label: 'Public' },
      { value: 'internal',     label: 'Internal' },
      { value: 'confidential', label: 'Confidential' },
      { value: 'restricted',   label: 'Restricted' },
    ],
  },
]

// ── Helpers ────────────────────────────────────────────────────────────────────

function nextRunLabel(schedId) {
  const hours = SCHEDULE_OPTIONS.find(s => s.id === schedId)?.hours ?? 6
  const next = new Date(Date.now() + hours * 3_600_000)
  return next.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function lastRunLabel(isoString) {
  if (!isoString) return null
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

// Fetches the original uploaded PDF for a job and opens it in a new tab — the reference
// point back from an extracted row/table to the source document it came from.
async function openPdfSource(apiFetch, jobId) {
  try {
    const res = await apiFetch(`/api/lake/uploads/pdf/${jobId}/source`)
    if (!res.ok) return
    const blob = await res.blob()
    window.open(URL.createObjectURL(blob), '_blank')
  } catch { /* non-fatal */ }
}

// ── Shared UI ──────────────────────────────────────────────────────────────────

function Breadcrumb({ parts }) {
  return (
    <div className={styles.breadcrumb}>
      {parts.map((p, i) => (
        <span key={i} className={styles.bcParts}>
          {i > 0 && <span className={styles.bcSep}>/</span>}
          {p.onClick
            ? <button className={styles.bcLink} onClick={p.onClick}>{p.label}</button>
            : <span className={styles.bcCurrent}>{p.label}</span>}
        </span>
      ))}
    </div>
  )
}

function Stat({ label, value, ok, small }) {
  return (
    <div className={styles.stat}>
      <span className={`${styles.statVal} ${ok ? styles.statOk : ''} ${small ? styles.statSmall : ''}`}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  )
}


// ── Run dbt modal ──────────────────────────────────────────────────────────────

function RunModal({ onClose, onJobStarted }) {
  const { apiFetch } = useAuth()
  const [mode, setMode] = useState('all')
  const [tags, setTags] = useState(new Set(['prepared', 'analytics']))
  const [submitting, setSubmitting] = useState(false)
  const [startErr, setStartErr] = useState('')

  const toggleTag = (t) => setTags(prev => {
    const next = new Set(prev)
    next.has(t) ? next.delete(t) : next.add(t)
    return next
  })

  const handleRun = async () => {
    setSubmitting(true)
    setStartErr('')
    try {
      const select = mode === 'tags' ? [...tags].join(' ') : undefined
      const res = await apiFetch('/api/lake/refresh', {
        method: 'POST',
        body: JSON.stringify({ command: 'run', target: 'staging', ...(select ? { select } : {}) }),
      })
      if (!res.ok) throw new Error('Failed to start refresh')
      const { job_id } = await res.json()
      onJobStarted(job_id)
      onClose()
    } catch (err) {
      setStartErr(err.message || 'Failed to start refresh')
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.runModal}>
        <div className={styles.runModalHeader}>
          <span className={styles.runModalTitle}>Refresh Lake</span>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div className={styles.runModalBody}>
          <div className={styles.runSection}>
            <div className={styles.runSectionLabel}>What would you like to refresh?</div>
            <label className={styles.radioRow}>
              <input type="radio" checked={mode === 'all'} onChange={() => setMode('all')} />
              Full run — all models
            </label>
            <label className={styles.radioRow}>
              <input type="radio" checked={mode === 'tags'} onChange={() => setMode('tags')} />
              Select layers
            </label>
            {mode === 'tags' && (
              <div className={styles.tagChips}>
                {DBT_TAGS.map(t => (
                  <button
                    key={t}
                    className={`${styles.tagChip} ${tags.has(t) ? styles.tagChipOn : ''}`}
                    onClick={() => toggleTag(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
          {startErr && <div className={styles.startErr}>{startErr}</div>}
          <button
            className={styles.runBtn}
            onClick={handleRun}
            disabled={submitting || (mode === 'tags' && tags.size === 0)}
          >
            {submitting ? '…' : '▶ Refresh Lake'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Refresh status bar ─────────────────────────────────────────────────────────

function RefreshStatusBar({ job, onDismiss, openChat }) {
  if (!job) return null
  const { state, errorId } = job

  const cls = state === 'done' ? styles.statusBarDone
    : state === 'failed' ? styles.statusBarFailed
    : styles.statusBarRunning

  return (
    <div className={`${styles.statusBar} ${cls}`}>
      <span className={styles.statusBarMsg}>
        {state === 'running'   && <><span className={styles.statusSpinner} /> Refreshing lake…</>}
        {state === 'resolving' && <><span className={styles.statusSpinner} /> Attempting auto-resolve…</>}
        {state === 'done'      && '✓ Lake refreshed successfully'}
        {state === 'failed'    && `✕ Refresh failed — Error ID: ${errorId}`}
      </span>
      <div className={styles.statusBarActions}>
        {state === 'failed' && openChat && (
          <button
            className={styles.statusBarFixBtn}
            onClick={() => openChat(`Refresh failed — Error ID: ${errorId}. Please diagnose and fix this.`)}
          >
            ✦ Fix with AI
          </button>
        )}
        {(state === 'done' || state === 'failed') && (
          <button className={styles.statusBarDismiss} onClick={onDismiss}>✕</button>
        )}
      </div>
    </div>
  )
}

// ── Schedule editor ────────────────────────────────────────────────────────────

function ScheduleBar({ schedule, lastRun, onSave }) {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving]   = useState(false)
  const [draft, setDraft]     = useState(schedule)
  const opt = SCHEDULE_OPTIONS.find(s => s.id === schedule)

  const save = async () => {
    setSaving(true)
    await onSave(draft)
    setSaving(false)
    setEditing(false)
  }

  const lastRunText = lastRunLabel(lastRun)
  const isOff = schedule === 'off' || !schedule

  return (
    <div className={styles.scheduleBar}>
      {!editing ? (
        <>
          <span className={styles.scheduleIcon}>⏱</span>
          <span className={styles.scheduleText}>
            Scheduled: <strong>{opt?.label ?? (isOff ? 'Off — manual only' : schedule)}</strong>
          </span>
          {lastRunText && (
            <>
              <span className={styles.scheduleSep}>·</span>
              <span className={styles.scheduleNext}>Last run {lastRunText}</span>
            </>
          )}
          {!isOff && (
            <>
              <span className={styles.scheduleSep}>·</span>
              <span className={styles.scheduleNext}>Next ~{nextRunLabel(schedule)}</span>
            </>
          )}
          <button className={styles.scheduleEditBtn} onClick={() => setEditing(true)}>Edit schedule</button>
        </>
      ) : (
        <div className={styles.scheduleEditPanel}>
          <div className={styles.scheduleEditLabel}>Run frequency</div>
          <div className={styles.scheduleOptions}>
            {SCHEDULE_OPTIONS.map(opt => (
              <label key={opt.id} className={`${styles.scheduleOption} ${draft === opt.id ? styles.scheduleOptionActive : ''}`}>
                <input
                  type="radio"
                  name="schedule"
                  value={opt.id}
                  checked={draft === opt.id}
                  onChange={() => setDraft(opt.id)}
                />
                <span>{opt.label}</span>
                <span className={styles.scheduleCron}>{opt.cron}</span>
              </label>
            ))}
          </div>
          <div className={styles.scheduleActions}>
            <button className={styles.scheduleSaveBtn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button className={styles.scheduleCancelBtn} onClick={() => { setDraft(schedule); setEditing(false) }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Overview ───────────────────────────────────────────────────────────────────

function OverviewView({ sources, prepared, analytics, seeds, onNav, schedule, lastDbtRun, onSaveSchedule, onRunOpen }) {
  const sourceTableCount   = sources.reduce((n, s) => n + s.tables.length, 0)
  const preparedModelCount = prepared.reduce((n, s) => n + s.models.length, 0)
  const hasModels = preparedModelCount > 0 || analytics.length > 0

  const layers = [
    { id: 'sources',   label: 'RAW',  color: 'faint', name: 'Raw',
      subtitle: 'Untouched data straight from your connectors',
      meta: `${sources.length} connectors · ${sourceTableCount} tables` },
    { id: 'prepared',  label: 'PREP', color: 'sky',   name: 'Prepared',
      subtitle: 'Cleaned and standardized, ready to query',
      meta: `${prepared.length} connectors · ${preparedModelCount} models` },
    { id: 'analytics', label: 'ANA',  color: 'teal',  name: 'Analytics',
      subtitle: 'Business-ready reports and metrics',
      meta: analytics.length ? `${analytics.length} models` : '0 models' },
    { id: 'uploads',   label: 'UPL',  color: 'warn',  name: 'Uploads',
      subtitle: 'CSV files and PDF documents loaded into your data lake',
      meta: `${seeds.length} files` },
  ]

  return (
    <div className={styles.viewRoot}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Catalog</h1>
          <p className={styles.sub}>Browse and manage your data sources, models, and business metrics</p>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.headerStats}>
            <Stat label="Models" value={preparedModelCount + analytics.length} />
          </div>
          {hasModels && (
            <button className={styles.runOpenBtn} onClick={onRunOpen}>▶ Refresh Lake</button>
          )}
        </div>
      </div>

      {hasModels && <ScheduleBar schedule={schedule} lastRun={lastDbtRun} onSave={onSaveSchedule} />}

      <div className={styles.layerGrid}>
        {layers.map(l => (
          <button
            key={l.id}
            className={`${styles.layerCard} ${styles[`layerCard_${l.color}`]}`}
            onClick={() => onNav(l.id)}
          >
            <div className={styles.layerCardTop}>
              <span className={`${styles.layerLabel} ${styles[`layerLabel_${l.color}`]}`}>{l.label}</span>
              <span className={styles.layerArrow}>→</span>
            </div>
            <div className={styles.layerName}>{l.name}</div>
            <div className={styles.layerSubtitle}>{l.subtitle}</div>
            <div className={styles.layerMeta}>{l.meta}</div>
          </button>
        ))}
      </div>

    </div>
  )
}

// ── Data Wizard button ─────────────────────────────────────────────────────────

function WizardBtn({ wizardJob, onWizard }) {
  const running = wizardJob && wizardJob.status !== 'success' && wizardJob.status !== 'failed'
  const done    = wizardJob?.status === 'success'
  const failed  = wizardJob?.status === 'failed'
  const cls = [
    styles.wizardBtn,
    running ? styles.wizardBtnRunning : '',
    done    ? styles.wizardBtnDone    : '',
    failed  ? styles.wizardBtnFail    : '',
  ].filter(Boolean).join(' ')
  const label = running ? '◈ Running…' : done ? '◈ Prepared ✓' : failed ? '◈ Retry Wizard' : '◈ Data Wizard'
  return (
    <button className={cls} onClick={onWizard} disabled={running} title="Run Data Wizard">
      {label}
    </button>
  )
}

// ── Sources view ───────────────────────────────────────────────────────────────

function SourcesView({ sources, onBack, onSelectTable, wizardJobs, onWizard }) {
  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[{ label: 'Catalog', onClick: onBack }, { label: 'Raw' }]} />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Raw</h1>
          <p className={styles.sub}>Tables synced directly from connected systems. Declared as dbt sources.</p>
        </div>
        <span className={`${styles.layerLabel} ${styles.layerLabel_faint}`}>RAW</span>
      </div>
      {sources.length === 0 && (
        <div className={styles.emptyState}>
          No raw source tables yet. Connect a data source and run a sync to populate this view.
        </div>
      )}
      {sources.map(group => (
        <section key={group.connector} className={styles.section}>
          <div className={styles.connectorGroupLabel}>
            <span className={styles.connectorDot} />
            {group.connector}
            {onWizard && (
              <WizardBtn wizardJob={wizardJobs?.[group.connector]} onWizard={() => onWizard(group.connector)} />
            )}
          </div>
          <table className={styles.table}>
            <thead><tr><th>Table</th><th>Rows</th><th>Last synced</th><th></th></tr></thead>
            <tbody>
              {group.tables.map(t => (
                <tr key={t.id} className={styles.tableRow} onClick={() => onSelectTable?.(t, group.connector)}>
                  <td><span className={styles.modelName}>{t.name}</span></td>
                  <td className={styles.mono}>{typeof t.rows === 'number' ? Number(t.rows).toLocaleString() : t.rows}</td>
                  <td className={styles.monoMuted}>{t.lastSync}</td>
                  <td className={styles.arrowCell}>→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  )
}

// ── Raw table detail view ──────────────────────────────────────────────────────

function RawTableDetailView({ table, connectorLabel, onBack, onBackToSources }) {
  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[
        { label: 'Catalog',  onClick: onBack },
        { label: 'Raw',      onClick: onBackToSources },
        { label: table.name },
      ]} />
      <div className={styles.detailHeader}>
        <div className={styles.detailLeft}>
          <div className={styles.detailTop}>
            <h1 className={styles.title}>{table.name}</h1>
            <span className={styles.sourceTag}>{connectorLabel}</span>
          </div>
          <p className={styles.detailDesc}>
            {table.description || <em className={styles.noDesc}>No description — run a sync to auto-document this table</em>}
          </p>
        </div>
        <div className={styles.detailStats}>
          <Stat label="Rows"   value={typeof table.rows === 'number' ? Number(table.rows).toLocaleString() : table.rows || '—'} />
          <div className={styles.statDivider} />
          <Stat label="Synced" value={table.lastSync || '—'} small />
        </div>
      </div>
      {(table.columns || []).length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Columns</div>
          <table className={styles.table}>
            <thead>
              <tr><th>Column</th><th>Type</th><th>Description</th></tr>
            </thead>
            <tbody>
              {table.columns.map(col => (
                <tr key={col.name}>
                  <td><span className={styles.colName}>{col.name}</span></td>
                  <td><span className={styles.typeBadge}>{col.type || 'string'}</span></td>
                  <td className={styles.descCell}>
                    <span className={styles.colDesc}>
                      {col.description || <em className={styles.noDesc}>No description</em>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}

// ── Prepared view ──────────────────────────────────────────────────────────────

function PreparedView({ prepared: initialPrepared, onBack, onSelectModel, wizardJobs, onWizard }) {
  const { apiFetch }                = useAuth()
  const [prepared, setPrepared]     = useState(initialPrepared)
  const [confirmId, setConfirmId]   = useState(null)
  const [deleting, setDeleting]     = useState(null)
  const [deleteErr, setDeleteErr]   = useState(null)

  const handleDelete = async (modelName) => {
    setDeleting(modelName)
    setConfirmId(null)
    setDeleteErr(null)
    try {
      const res = await apiFetch('/api/lake/model', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model_name: modelName, model_type: 'prepared' }),
      })
      if (res.status === 409) {
        const { conflicts } = await res.json()
        const names = conflicts.map(c => `${c.type} "${c.label}"`).join(', ')
        setDeleteErr(`Cannot delete — referenced by ${names}. Remove those first.`)
        return
      }
      setPrepared(prev => prev.map(g => ({ ...g, models: g.models.filter(m => m.id !== modelName) }))
        .filter(g => g.models.length > 0))
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[{ label: 'Catalog', onClick: onBack }, { label: 'Prepared' }]} />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Prepared</h1>
          <p className={styles.sub}>Cleaned and typed data from each source, organized by connector.</p>
        </div>
        <span className={`${styles.layerLabel} ${styles.layerLabel_sky}`}>PREP</span>
      </div>
      {deleteErr && <div className={styles.uploadErrBanner}>{deleteErr}</div>}
      {prepared.length === 0 && (
        <div className={styles.emptyState}>
          No prepared models yet. Ask the AI assistant to create prepared models from your source tables.
        </div>
      )}
      {prepared.map(group => (
        <section key={group.connector} className={styles.section}>
          <div className={styles.connectorGroupLabel}>
            <span className={styles.connectorDot} />
            {group.connector}
            {onWizard && (
              <WizardBtn wizardJob={wizardJobs?.[group.connector]} onWizard={() => onWizard(group.connector)} />
            )}
          </div>
          <table className={styles.table}>
            <thead><tr><th>Model</th><th>Rows</th><th>Last refreshed</th><th></th><th></th></tr></thead>
            <tbody>
              {group.models.map(m => (
                <tr key={m.id} className={styles.tableRow}>
                  <td onClick={() => onSelectModel(m, group.connector)}><span className={styles.modelName}>{m.name}</span></td>
                  <td className={styles.mono} onClick={() => onSelectModel(m, group.connector)}>{m.rows ? Number(m.rows).toLocaleString() : '—'}</td>
                  <td className={styles.monoMuted} onClick={() => onSelectModel(m, group.connector)}>{m.lastRefreshed || '—'}</td>
                  <td className={styles.arrowCell} onClick={() => onSelectModel(m, group.connector)}>→</td>
                  <td className={styles.deleteCell} onClick={e => e.stopPropagation()}>
                    {confirmId === m.id ? (
                      <>
                        <button className={styles.confirmDeleteBtn} onClick={() => handleDelete(m.id)} disabled={!!deleting}>
                          {deleting === m.id ? '…' : 'Confirm'}
                        </button>
                        <button className={styles.cancelDeleteBtn} onClick={() => setConfirmId(null)}>Cancel</button>
                      </>
                    ) : (
                      <button className={styles.deleteBtn} onClick={() => setConfirmId(m.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  )
}

// ── Analytics view ─────────────────────────────────────────────────────────────

function AnalyticsView({ analytics: initialAnalytics, onBack, onSelectModel, widgetsByModel = {} }) {
  const { apiFetch }                  = useAuth()
  const [analytics, setAnalytics]     = useState(initialAnalytics)
  const [confirmId, setConfirmId]     = useState(null)
  const [deleting, setDeleting]       = useState(null)
  const [deleteErr, setDeleteErr]     = useState(null)

  const handleDelete = async (m) => {
    setDeleting(m.id)
    setConfirmId(null)
    setDeleteErr(null)
    try {
      const res = await apiFetch('/api/lake/model', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model_name: m.name, layer: m.source, model_type: 'analytics' }),
      })
      if (res.status === 409) {
        const { conflicts } = await res.json()
        const names = conflicts.map(c => `${c.type} "${c.label}"`).join(', ')
        setDeleteErr(`Cannot delete — referenced by ${names}. Remove those first.`)
        return
      }
      setAnalytics(prev => prev.filter(a => a.id !== m.id))
    } finally {
      setDeleting(null)
    }
  }

  const coreModels   = analytics.filter(m => m.name.startsWith('dim_') || m.name.startsWith('fct_'))
  const reportModels = analytics.filter(m => m.name.startsWith('rpt_'))
  const kpiModels    = analytics.filter(m => m.name.startsWith('kpi_'))
  const otherModels  = analytics.filter(m =>
    !m.name.startsWith('dim_') && !m.name.startsWith('fct_') &&
    !m.name.startsWith('rpt_') && !m.name.startsWith('kpi_')
  )

  const ModelTable = ({ models }) => (
    <table className={styles.table}>
      <thead><tr><th>Model</th><th>Layer</th><th>Rows</th><th>Last refreshed</th><th>Status</th><th></th><th></th></tr></thead>
      <tbody>
        {models.map(m => (
          <tr key={m.id} className={styles.tableRow}>
            <td onClick={() => onSelectModel(m, 'analytics')}>
              <span className={styles.modelName}>{m.name}</span>
              {widgetsByModel[m.name] && (
                <span className={styles.chartBadge} title={`Dashboard: "${widgetsByModel[m.name]}"`}>
                  ◈ {widgetsByModel[m.name]}
                </span>
              )}
            </td>
            <td onClick={() => onSelectModel(m, 'analytics')}><span className={styles.sourceTag}>{m.source}</span></td>
            <td className={styles.mono} onClick={() => onSelectModel(m, 'analytics')}>{m.rows != null ? Number(m.rows).toLocaleString() : '—'}</td>
            <td className={styles.monoMuted} onClick={() => onSelectModel(m, 'analytics')}>{m.lastRefreshed}</td>
            <td onClick={() => onSelectModel(m, 'analytics')}>
              {m.columns?.length > 0
                ? <span className={styles.docsBadge}>Documented</span>
                : <span className={styles.docsBadgeMissing}>Needs docs</span>}
            </td>
            <td className={styles.arrowCell} onClick={() => onSelectModel(m, 'analytics')}>→</td>
            <td className={styles.deleteCell} onClick={e => e.stopPropagation()}>
              {confirmId === m.id ? (
                <>
                  <button className={styles.confirmDeleteBtn} onClick={() => handleDelete(m)} disabled={!!deleting}>
                    {deleting === m.id ? '…' : 'Confirm'}
                  </button>
                  <button className={styles.cancelDeleteBtn} onClick={() => setConfirmId(null)}>Cancel</button>
                </>
              ) : (
                <button className={styles.deleteBtn} onClick={() => setConfirmId(m.id)}>Delete</button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[{ label: 'Catalog', onClick: onBack }, { label: 'Analytics' }]} />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Analytics</h1>
          <p className={styles.sub}>Business-ready tables combining data across sources with validated metrics.</p>
        </div>
        <span className={`${styles.layerLabel} ${styles.layerLabel_teal}`}>ANA</span>
      </div>
      {deleteErr && <div className={styles.uploadErrBanner}>{deleteErr}</div>}
      {analytics.length === 0 ? (
        <div className={styles.emptyState}>
          No analytics models yet. Ask the AI assistant to build analytics models from your prepared data.
        </div>
      ) : (
        <>
          {coreModels.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>Core</div>
              <ModelTable models={coreModels} />
            </section>
          )}
          {reportModels.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>Reports</div>
              <ModelTable models={reportModels} />
            </section>
          )}
          {kpiModels.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>KPIs</div>
              <ModelTable models={kpiModels} />
            </section>
          )}
          {otherModels.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>Other</div>
              <ModelTable models={otherModels} />
            </section>
          )}
        </>
      )}
    </div>
  )
}

// ── Seeds view ─────────────────────────────────────────────────────────────────

function SeedsView({ seeds: initialSeeds, onBack, onBackToUploads, openChat, onRefresh, onSelectSeed }) {
  const { apiFetch }                = useAuth()
  const [seeds, setSeeds]           = useState(initialSeeds)
  const [confirmId, setConfirmId]   = useState(null)
  const [deleting, setDeleting]     = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [uploading, setUploading]   = useState(false)
  const [uploadErr, setUploadErr]   = useState(null)
  const [uploadOk, setUploadOk]     = useState(null)
  const [defJobs, setDefJobs]       = useState({})
  const [conflict, setConflict]     = useState(null) // { file, seedName, existing, suggestedName }
  const fileInputRef                = useRef(null)

  const nextVersionName = (name) => {
    const m = name.match(/^(.+?)_v(\d+)$/)
    return m ? `${m[1]}_v${parseInt(m[2], 10) + 1}` : `${name}_v2`
  }

  const handleRefresh = async () => {
    if (!onRefresh) return
    setRefreshing(true)
    try { await onRefresh() } finally { setRefreshing(false) }
  }

  const handleDelete = async (seedName) => {
    setDeleting(seedName)
    setConfirmId(null)
    try {
      await apiFetch(`/api/lake/seeds/${encodeURIComponent(seedName)}`, { method: 'DELETE' })
      setSeeds(prev => prev.filter(s => s.name !== seedName))
    } finally {
      setDeleting(null)
    }
  }

  const pollDefJob = (seedName, jobId) => {
    const timer = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/definition-jobs/${jobId}`)
        if (!res.ok) { clearInterval(timer); return }
        const job = await res.json()
        setDefJobs(prev => ({ ...prev, [seedName]: { jobId, status: job.status } }))
        if (job.status === 'success' || job.status === 'failed') {
          clearInterval(timer)
          if (job.status === 'success') {
            setTimeout(() => onRefresh?.(), 1500) // reload to pick up generated description
            setTimeout(() => setDefJobs(prev => { const n = { ...prev }; delete n[seedName]; return n }), 6000)
          }
        }
      } catch { clearInterval(timer) }
    }, 3000)
  }

  const doUpload = async (file, uploadFilename, autoPromote = false) => {
    setUploadErr(null)
    setUploading(true)
    try {
      const content = await file.text()
      const res = await apiFetch('/api/lake/seeds', {
        method:  'POST',
        headers: { 'Content-Type': 'text/csv', 'X-Filename': uploadFilename },
        body:    content,
      })
      if (!res.ok) {
        const { error } = await res.json().catch(() => ({}))
        throw new Error(error || 'Upload failed')
      }
      const { filename, defJobId, defJobError } = await res.json()
      const seedName = filename.replace(/\.csv$/i, '')
      setSeeds(prev => prev.some(s => s.name === seedName)
        ? prev.map(s => s.name === seedName ? { ...s, rows: null, uploaded: 'just now' } : s)
        : [{ id: filename, name: seedName, rows: null, uploaded: 'just now', description: '' }, ...prev]
      )
      if (defJobId) {
        setDefJobs(prev => ({ ...prev, [seedName]: { jobId: defJobId, status: 'queued' } }))
        pollDefJob(seedName, defJobId)
      }
      if (autoPromote) {
        setUploadOk('Replacing and promoting to production…')
        try {
          const proRes = await apiFetch(`/api/lake/seeds/${encodeURIComponent(seedName)}/promote`, { method: 'POST' })
          if (!proRes.ok) {
            const { error } = await proRes.json().catch(() => ({}))
            throw new Error(error || 'Promotion failed')
          }
          const { jobId: promoteJobId } = await proRes.json()
          const timer = setInterval(async () => {
            try {
              const poll = await apiFetch(`/api/lake/refresh/${promoteJobId}`)
              if (!poll.ok) { clearInterval(timer); return }
              const job = await poll.json()
              if (job.status === 'success') {
                clearInterval(timer)
                setSeeds(prev => prev.map(s => s.name === seedName ? { ...s, promoted: true } : s))
                setUploadOk('Replaced and promoted to production.')
                setTimeout(() => setUploadOk(null), 6000)
              } else if (job.status === 'failed') {
                clearInterval(timer)
                setUploadOk(null)
                setUploadErr(`Uploaded but promotion failed: ${job.error_detail || 'check logs'}`)
              }
            } catch { clearInterval(timer); setUploadOk(null) }
          }, 3000)
        } catch (err) {
          setUploadOk(null)
          setUploadErr(`Uploaded but promotion failed: ${err.message}`)
        }
      } else if (defJobId) {
        setUploadOk('Upload complete — AI is documenting your columns…')
        setTimeout(() => setUploadOk(null), 6000)
      } else {
        setUploadErr(
          defJobError
            ? `File uploaded, but AI documentation failed: ${defJobError}. Ask the AI to document "${seedName}" manually.`
            : 'File uploaded. Ask the AI to document the columns for this table.'
        )
      }
    } catch (err) {
      setUploadErr(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    const rawName = file.name.replace(/\.csv$/i, '').replace(/[^a-zA-Z0-9_]/g, '_').replace(/^_+|_+$/g, '') || 'upload'
    const existing = seeds.find(s => s.name === rawName)
    if (existing) {
      setConflict({ file, seedName: rawName, existing, suggestedName: nextVersionName(rawName) })
      return
    }
    doUpload(file, file.name)
  }

  const handleConflict = (action) => {
    const { file, seedName, existing, suggestedName } = conflict
    setConflict(null)
    if (action === 'replace')     doUpload(file, `${seedName}.csv`, existing.promoted === true)
    if (action === 'new_version') doUpload(file, `${suggestedName}.csv`)
  }

  // Sync local seeds state when parent refreshes
  useEffect(() => { setSeeds(initialSeeds) }, [initialSeeds])

  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[{ label: 'Catalog', onClick: onBack }, { label: 'Uploads', onClick: onBackToUploads }, { label: 'CSV Files' }]} />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>CSV Files</h1>
          <p className={styles.sub}>CSV files loaded into your data lake as queryable reference tables.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {onRefresh && (
            <button
              className={styles.cancelDeleteBtn}
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh list"
            >
              {refreshing ? '…' : '↻ Refresh'}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <button
            className={styles.uploadBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : '↑ Upload CSV'}
          </button>
          <span className={`${styles.layerLabel} ${styles.layerLabel_warn}`}>CSV</span>
        </div>
      </div>

      {uploadErr && (
        <div className={styles.uploadErrBanner}>{uploadErr}</div>
      )}
      {uploadOk && (
        <div className={styles.uploadOkBanner}>{uploadOk}</div>
      )}

      {conflict && (
        <div className={styles.conflictDialog}>
          <div className={styles.conflictMsg}>
            <strong>{conflict.seedName}</strong> already exists
            {typeof conflict.existing.rows === 'number' ? ` (${conflict.existing.rows.toLocaleString()} rows)` : ''}.
            {' '}How would you like to proceed?
          </div>
          <div className={styles.conflictActions}>
            <button className={styles.conflictReplace} onClick={() => handleConflict('replace')}>
              Replace existing
            </button>
            <button className={styles.conflictVersion} onClick={() => handleConflict('new_version')}>
              Save as <code>{conflict.suggestedName}</code>
            </button>
            <button className={styles.cancelDeleteBtn} onClick={() => setConflict(null)}>Cancel</button>
          </div>
        </div>
      )}

      <section className={styles.section}>
        {seeds.length === 0 ? (
          <div className={styles.emptyState}>No reference files yet. Click &quot;↑ Upload CSV&quot; to add one.</div>
        ) : (
          <table className={styles.table}>
            <thead><tr><th>File</th><th>Rows</th><th>Uploaded</th><th>Description</th><th></th><th></th><th></th></tr></thead>
            <tbody>
              {seeds.map(s => {
                const job = defJobs[s.name]
                return (
                  <tr key={s.id} className={styles.tableRow}>
                    <td onClick={() => onSelectSeed?.(s)}>
                      <span className={styles.modelName}>{s.name}</span>
                      {job && (
                        <span className={
                          job.status === 'success' ? styles.docBadgeDone :
                          job.status === 'failed'  ? styles.docBadgeFail : styles.docBadge
                        }>
                          {job.status === 'success' ? 'Documented ✓' :
                           job.status === 'failed'  ? 'Doc error'    : 'Documenting…'}
                        </span>
                      )}
                    </td>
                    <td className={styles.mono} onClick={() => onSelectSeed?.(s)}>{typeof s.rows === 'number' ? Number(s.rows).toLocaleString() : '—'}</td>
                    <td className={styles.monoMuted} onClick={() => onSelectSeed?.(s)}>{s.uploaded}</td>
                    <td className={styles.monoMuted} onClick={() => onSelectSeed?.(s)}>{s.description || <em className={styles.noDesc}>—</em>}</td>
                    <td onClick={() => onSelectSeed?.(s)}>
                      <span className={s.promoted ? styles.envBadgeProd : styles.envBadgeStaged}>
                        {s.promoted ? 'Production' : 'Staged'}
                      </span>
                    </td>
                    <td className={styles.arrowCell} onClick={() => onSelectSeed?.(s)}>→</td>
                    <td className={styles.deleteCell} onClick={e => e.stopPropagation()}>
                      {confirmId === s.name ? (
                        <>
                          <button className={styles.confirmDeleteBtn} onClick={() => handleDelete(s.name)} disabled={!!deleting}>
                            {deleting === s.name ? '…' : 'Confirm'}
                          </button>
                          <button className={styles.cancelDeleteBtn} onClick={() => setConfirmId(null)}>Cancel</button>
                        </>
                      ) : (
                        <button className={styles.deleteBtn} onClick={() => setConfirmId(s.name)}>Delete</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      {seeds.length === 0 && (
        <div className={styles.chatUploadPrompt} style={{ marginTop: '1.5rem' }}>
          <span className={styles.chatUploadIcon}>◈</span>
          <div>
            <div className={styles.chatUploadTitle}>Ask the AI for help with reference data</div>
            <div className={styles.chatUploadSub}>Describe what you want to add — the AI can guide you through naming, data types, and relationships.</div>
          </div>
          <button
            className={styles.chatUploadBtn}
            onClick={() => openChat('I want to add a reference data CSV file')}
          >
            Open chat →
          </button>
        </div>
      )}
    </div>
  )
}

// ── Seed detail ────────────────────────────────────────────────────────────────

function SeedDetailView({ seed, onBack, onBackToUploads, onBackToSeeds }) {
  const { apiFetch } = useAuth()
  const [seedDesc, setSeedDesc]         = useState(seed.description)
  const [editingDesc, setEditingDesc]   = useState(false)
  const [colEdits, setColEdits]         = useState(() =>
    Object.fromEntries((seed.columns || []).map(c => [c.name, c.description || '']))
  )
  const [editingCol, setEditingCol]     = useState(null)
  const [draftDesc, setDraftDesc]       = useState('')
  const [saving, setSaving]             = useState(false)
  const [saved, setSaved]               = useState(null)
  const [saveErr, setSaveErr]           = useState(null)
  const [promoting, setPromoting]       = useState(false)
  const [promoteErr, setPromoteErr]     = useState(null)
  const [promoted, setPromoted]         = useState(seed.promoted ?? false)
  const [rowCols, setRowCols]           = useState([])
  const [rows, setRows]                 = useState([])
  const [rowsTotal, setRowsTotal]       = useState(0)
  const [rowsPage, setRowsPage]         = useState(1)
  const [rowsLoading, setRowsLoading]   = useState(true)
  const [rowsErr, setRowsErr]           = useState(null)
  const ROWS_PAGE_SIZE = 25

  // Sync from props when the parent refreshes after background documentation
  useEffect(() => {
    setSeedDesc(prev => prev || seed.description || '')
  }, [seed.description])
  useEffect(() => { setPromoted(seed.promoted ?? false) }, [seed.promoted])
  useEffect(() => {
    setColEdits(prev => {
      if (Object.keys(prev).length === 0 && (seed.columns || []).length > 0) {
        return Object.fromEntries((seed.columns || []).map(c => [c.name, c.description || '']))
      }
      return prev
    })
  }, [seed.columns])

  const saveSeedDesc = async () => {
    setSaving(true); setSaveErr(null)
    try {
      const res = await apiFetch(`/api/lake/seeds/${encodeURIComponent(seed.name)}/description`, {
        method: 'PATCH',
        body: JSON.stringify({ description: seedDesc }),
      })
      if (!res.ok) { const { error } = await res.json().catch(() => ({})); throw new Error(error || 'Save failed') }
      setEditingDesc(false)
      setSaved('__seed__')
      setTimeout(() => setSaved(null), 2000)
    } catch (err) { setSaveErr(err.message || 'Save failed') }
    finally { setSaving(false) }
  }

  const startEditCol = (col) => { setEditingCol(col.name); setDraftDesc(colEdits[col.name] || ''); setSaveErr(null) }
  const cancelEditCol = () => { setEditingCol(null); setDraftDesc('') }

  const saveCol = async () => {
    if (!editingCol) return
    setSaving(true); setSaveErr(null)
    try {
      const res = await apiFetch(`/api/lake/seeds/${encodeURIComponent(seed.name)}/column`, {
        method: 'PATCH',
        body: JSON.stringify({ column_name: editingCol, description: draftDesc }),
      })
      if (!res.ok) { const { error } = await res.json().catch(() => ({})); throw new Error(error || 'Save failed') }
      const committed = editingCol
      setColEdits(e => ({ ...e, [committed]: draftDesc }))
      setEditingCol(null); setDraftDesc('')
      setSaved(committed)
      setTimeout(() => setSaved(null), 2000)
    } catch (err) { setSaveErr(err.message || 'Save failed') }
    finally { setSaving(false) }
  }

  const handlePromote = async () => {
    setPromoting(true)
    setPromoteErr(null)
    try {
      const res = await apiFetch(`/api/lake/seeds/${encodeURIComponent(seed.name)}/promote`, { method: 'POST' })
      if (!res.ok) {
        const { error } = await res.json().catch(() => ({}))
        throw new Error(error || 'Promotion failed')
      }
      const { jobId } = await res.json()
      // Poll compile job until success/failed
      const timer = setInterval(async () => {
        try {
          const poll = await apiFetch(`/api/lake/refresh/${jobId}`)
          if (!poll.ok) { clearInterval(timer); return }
          const job = await poll.json()
          if (job.status === 'success') {
            clearInterval(timer)
            setPromoted(true)
            setPromoting(false)
          } else if (job.status === 'failed') {
            clearInterval(timer)
            setPromoteErr(job.error_detail || 'Promotion failed — check logs')
            setPromoting(false)
          }
        } catch { clearInterval(timer); setPromoting(false) }
      }, 3000)
    } catch (err) {
      setPromoteErr(err.message || 'Promotion failed')
      setPromoting(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    setRowsLoading(true); setRowsErr(null)
    apiFetch(`/api/lake/seeds/${encodeURIComponent(seed.name)}/rows?page=${rowsPage}&pageSize=${ROWS_PAGE_SIZE}`)
      .then(async res => {
        if (!res.ok) { const { error } = await res.json().catch(() => ({})); throw new Error(error || 'Failed to load rows') }
        return res.json()
      })
      .then(data => {
        if (cancelled) return
        setRowCols(data.columns || [])
        setRows(data.rows || [])
        setRowsTotal(data.totalRows || 0)
      })
      .catch(err => { if (!cancelled) setRowsErr(err.message || 'Failed to load rows') })
      .finally(() => { if (!cancelled) setRowsLoading(false) })
    return () => { cancelled = true }
  }, [seed.name, rowsPage])

  const cols = seed.columns || []

  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[
        { label: 'Catalog',   onClick: onBack },
        { label: 'Uploads',   onClick: onBackToUploads },
        { label: 'CSV Files', onClick: onBackToSeeds },
        { label: seed.name },
      ]} />

      <div className={styles.detailHeader}>
        <div className={styles.detailLeft}>
          <div className={styles.detailTop}>
            <h1 className={styles.title}>{seed.name}</h1>
            <span className={`${styles.layerLabel} ${styles.layerLabel_warn}`}>CSV</span>
          </div>
          {editingDesc ? (
            <div className={styles.descEdit}>
              <textarea className={styles.descTextarea} rows={3} value={seedDesc}
                onChange={e => setSeedDesc(e.target.value)} autoFocus />
              <div className={styles.descActions}>
                <button className={styles.saveBtn} onClick={saveSeedDesc} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button className={styles.cancelBtn} onClick={() => setEditingDesc(false)} disabled={saving}>Cancel</button>
                {saveErr && <span className={styles.saveErrMsg}>{saveErr}</span>}
              </div>
            </div>
          ) : (
            <div className={styles.descRow}>
              <p className={styles.detailDesc}>{seedDesc || <em className={styles.noDesc}>No description yet — ask the AI to document this table, or edit manually</em>}</p>
              <button className={styles.editBtn} onClick={() => setEditingDesc(true)}>Edit</button>
              {saved === '__seed__' && <span className={styles.savedMsg}>✓ Saved</span>}
            </div>
          )}
        </div>
        <div className={styles.detailStats}>
          <Stat label="Rows"     value={typeof seed.rows === 'number' ? Number(seed.rows).toLocaleString() : '—'} />
          {typeof seed.columnCount === 'number' && (
            <>
              <div className={styles.statDivider} />
              <Stat label="Columns" value={seed.columnCount} />
            </>
          )}
          <div className={styles.statDivider} />
          <Stat label="Uploaded" value={seed.uploaded || '—'} small />
        </div>
      </div>

      <div className={promoted ? styles.prodBanner : styles.stageBanner}>
        <div className={styles.bannerLeft}>
          <span className={promoted ? styles.envBadgeProd : styles.envBadgeStaged}>
            {promoted ? 'Production' : 'Staged'}
          </span>
          <span className={styles.bannerMsg}>
            {promoted
              ? 'This table is in production and queryable.'
              : 'This table is in staging only — promote to make it available in production.'}
          </span>
        </div>
        {!promoted && (
          <button
            className={styles.promoteBtn}
            onClick={handlePromote}
            disabled={promoting || !seedDesc}
            title={!seedDesc ? 'Add a table description before promoting' : undefined}
          >
            {promoting ? 'Promoting…' : '↑ Promote to Production'}
          </button>
        )}
      </div>
      {promoteErr && <div className={styles.uploadErrBanner}>{promoteErr}</div>}

      <section className={styles.section}>
        <div className={styles.sectionLabel}>Columns</div>
        {cols.length === 0 ? (
          <div className={styles.emptyState}>
            No column documentation yet — the AI will document this table after upload, or ask it directly.
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr><th>Column</th><th>Type</th><th>Description</th><th></th></tr>
            </thead>
            <tbody>
              {cols.map(col => {
                const isEditing = editingCol === col.name
                return (
                  <tr key={col.name}>
                    <td><span className={styles.colName}>{col.name}</span></td>
                    <td><span className={styles.typeBadge}>{col.type || 'varchar'}</span></td>
                    <td className={styles.descCell}>
                      {isEditing ? (
                        <div className={styles.colEditForm}>
                          <div className={styles.fieldRow}>
                            <span className={styles.fieldLabel}>Description</span>
                            <input
                              className={styles.descInput}
                              value={draftDesc}
                              onChange={e => setDraftDesc(e.target.value)}
                              placeholder="Business-friendly description"
                              autoFocus
                            />
                          </div>
                          {saveErr && <div className={styles.saveErrMsg}>{saveErr}</div>}
                        </div>
                      ) : (
                        <div className={styles.descRow}>
                          <span className={styles.colDesc}>
                            {colEdits[col.name] || <em className={styles.noDesc}>No description</em>}
                          </span>
                          {saved === col.name && <span className={styles.savedMsg}>✓ Saved</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <div className={styles.descActions}>
                          <button className={styles.saveBtn} onClick={saveCol} disabled={saving}>
                            {saving ? '…' : 'Save'}
                          </button>
                          <button className={styles.cancelBtn} onClick={cancelEditCol} disabled={saving}>✕</button>
                        </div>
                      ) : (
                        <button className={styles.editBtn} onClick={() => startEditCol(col)}>Edit</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionLabel}>Rows</div>
          {rowsTotal > 0 && (
            <span className={styles.sectionHint}>
              {((rowsPage - 1) * ROWS_PAGE_SIZE) + 1}–{Math.min(rowsPage * ROWS_PAGE_SIZE, rowsTotal)} of {rowsTotal.toLocaleString()}
            </span>
          )}
        </div>
        {rowsErr ? (
          <div className={styles.uploadErrBanner}>{rowsErr}</div>
        ) : rowsLoading ? (
          <div className={styles.emptyState}>Loading rows…</div>
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>No rows yet.</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {rowCols.map(c => <th key={c}>{c}</th>)}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {rowCols.map(c => <td key={c}>{row[c]}</td>)}
                      <td>
                        {row.source_pdf_job_id && (
                          <button className={styles.editBtn} onClick={() => openPdfSource(apiFetch, row.source_pdf_job_id)}>
                            View Source ↗
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rowsTotal > ROWS_PAGE_SIZE && (
              <div className={styles.descActions}>
                <button className={styles.cancelBtn} onClick={() => setRowsPage(p => Math.max(1, p - 1))} disabled={rowsPage <= 1}>← Prev</button>
                <button className={styles.cancelBtn} onClick={() => setRowsPage(p => p + 1)} disabled={rowsPage * ROWS_PAGE_SIZE >= rowsTotal}>Next →</button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}

// ── Uploads landing (CSV Files + PDF Documents) ────────────────────────────────

function UploadsView({ seeds, onBack, onCsvFiles, onPdfDocs }) {
  const subSections = [
    {
      id: 'csv', label: 'CSV', color: 'warn', name: 'CSV Files',
      subtitle: 'Spreadsheets and lookup tables as queryable reference data',
      meta: `${seeds.length} file${seeds.length !== 1 ? 's' : ''}`,
      onClick: onCsvFiles,
    },
    {
      id: 'pdf', label: 'PDF', color: 'sky', name: 'PDF Documents',
      subtitle: 'Order forms, purchase orders, delivery sheets — extracted into tables',
      meta: 'Upload to extract',
      onClick: onPdfDocs,
    },
  ]
  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[{ label: 'Catalog', onClick: onBack }, { label: 'Uploads' }]} />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Uploads</h1>
          <p className={styles.sub}>Load files into your data lake as queryable tables</p>
        </div>
        <span className={`${styles.layerLabel} ${styles.layerLabel_warn}`}>UPL</span>
      </div>
      <div className={styles.layerGrid}>
        {subSections.map(s => (
          <button key={s.id} className={`${styles.layerCard} ${styles[`layerCard_${s.color}`]}`} onClick={s.onClick}>
            <div className={styles.layerCardTop}>
              <span className={`${styles.layerLabel} ${styles[`layerLabel_${s.color}`]}`}>{s.label}</span>
              <span className={styles.layerArrow}>→</span>
            </div>
            <div className={styles.layerName}>{s.name}</div>
            <div className={styles.layerSubtitle}>{s.subtitle}</div>
            <div className={styles.layerMeta}>{s.meta}</div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── PDF Documents (Textract upload → schema review → commit) ───────────────────

function DocumentsView({ onBack, onBackToUploads }) {
  const { apiFetch }                 = useAuth()
  const [phase, setPhase]            = useState('idle')
  const [jobs, setJobs]              = useState([])
  const [schema, setSchema]          = useState([])
  const [allRows, setAllRows]        = useState([])
  const [extraText, setExtraText]    = useState([])
  const [tableName, setTableName]    = useState('')
  const [tableNameErr, setTableNameErr] = useState('')
  const [commitResult, setCommitResult] = useState(null)
  const [err, setErr]                = useState(null)
  const [appendMode, setAppendMode]  = useState(false)
  const [appendTo, setAppendTo]      = useState('')
  const [existingSeeds, setExistingSeeds] = useState([])
  const fileInputRef                 = useRef(null)
  const pollRef                      = useRef(null)

  // cleanup on unmount only — restore effect is defined after buildReview below
  useEffect(() => () => clearInterval(pollRef.current), [])

  const updateSchema = (idx, field, value) =>
    setSchema(prev => prev.map((col, i) => i === idx ? { ...col, [field]: value } : col))

  const viewSourcePdf = (jobId) => openPdfSource(apiFetch, jobId)

  const startUpload = async (files) => {
    setErr(null)
    setPhase('uploading')
    setJobs(files.map(f => ({ filename: f.name, status: 'uploading', jobId: null })))
    try {
      const uploaded = await Promise.all(files.map(async (file) => {
        const res = await apiFetch('/api/lake/uploads/pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/pdf', 'X-Filename': file.name },
          body: file,
        })
        if (!res.ok) { const { error } = await res.json().catch(() => ({})); throw new Error(error || `Upload failed for ${file.name}`) }
        const { jobId } = await res.json()
        return { filename: file.name, jobId, status: 'extracting' }
      }))
      setJobs(uploaded)
      setPhase('extracting')
      startPolling(uploaded)
    } catch (e) {
      setErr(e.message)
      setPhase('idle')
    }
  }

  const startPolling = (currentJobs) => {
    clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const updated = await Promise.all(currentJobs.map(async j => {
          if (j.status === 'ready' || j.status === 'failed') return j
          const res = await apiFetch(`/api/lake/uploads/pdf/${j.jobId}`)
          if (!res.ok) return { ...j, status: 'failed' }
          const data = await res.json()
          return { ...j, status: data.status, extracted: data.status === 'ready' ? data : null }
        }))
        setJobs(updated)
        if (updated.every(j => j.status === 'ready' || j.status === 'failed')) {
          clearInterval(pollRef.current)
          const ready = updated.filter(j => j.status === 'ready')
          if (!ready.length) { setErr('All documents failed to extract. Please try again.'); setPhase('idle'); return }
          await buildReview(ready)
        }
      } catch { /* network hiccup */ }
    }, 3000)
  }

  const buildReview = async (readyJobs) => {
    try {
      let mergedSchema, mergedRows, mergedExtraText
      if (readyJobs.length === 1) {
        const { schema: s, allRows: r, extraText: t } = readyJobs[0].extracted
        mergedSchema = s
        mergedRows   = r || []
        mergedExtraText = t || []
      } else {
        const res = await apiFetch('/api/lake/uploads/pdf/merge', {
          method: 'POST',
          body: JSON.stringify({ jobIds: readyJobs.map(j => j.jobId) }),
        })
        if (!res.ok) { const { error } = await res.json().catch(() => ({})); throw new Error(error || 'Failed to merge schemas') }
        const data = await res.json()
        mergedSchema = data.schema
        mergedRows   = data.allRows || []
        mergedExtraText = data.extraText || []
      }
      setSchema((mergedSchema || []).map(col => ({ ...col, include: true, targetName: col.normalized })))
      setAllRows(mergedRows)
      setExtraText(mergedExtraText)
      const baseName = readyJobs[0].filename.replace(/\.pdf$/i, '').replace(/[^a-z0-9]+/gi, '_').toLowerCase()
      setTableName(baseName)
      // Load existing seed tables for the append-mode dropdown
      apiFetch('/api/lake/seeds').then(async r => {
        if (!r.ok) return
        const { seeds = [] } = await r.json()
        setExistingSeeds(seeds.map(s => s.name || s.seed_name || s).filter(Boolean))
      }).catch(() => {})
      setPhase('review')
    } catch (e) {
      setErr(e.message)
      setPhase('idle')
    }
  }

  // Restore in-progress or completed jobs if the user navigated away mid-flow.
  // Only pick up jobs from the last 24 h that haven't been committed yet.
  useEffect(() => {
    let cancelled = false
    const MS_24H = 24 * 60 * 60 * 1000
    apiFetch('/api/lake/uploads/pdf').then(async res => {
      if (!res.ok || cancelled) return
      const { jobs: priorJobs = [] } = await res.json()
      const recent = priorJobs.filter(j =>
        !j.committed_at &&
        j.status === 'ready' &&
        Date.now() - new Date(j.created_at).getTime() < MS_24H
      )
      if (!recent.length || cancelled) return
      const hydrated = (await Promise.all(recent.map(async j => {
        const r = await apiFetch(`/api/lake/uploads/pdf/${j.jobId}`)
        if (!r.ok) return null
        const data = await r.json()
        return data.status === 'ready' ? { filename: j.filename, jobId: j.jobId, status: 'ready', extracted: data } : null
      }))).filter(Boolean)
      if (!hydrated.length || cancelled) return
      setJobs(hydrated)
      await buildReview(hydrated)
    }).catch(() => {})
    return () => { cancelled = true }
  // buildReview is stable (state setters are stable); apiFetch from useAuth is stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCommit = async () => {
    if (appendMode && !appendTo) { setTableNameErr('Select a table to append to'); return }
    if (!appendMode && !tableName.trim()) { setTableNameErr('Table name is required'); return }
    setPhase('committing')
    setErr(null)
    try {
      const res = await apiFetch('/api/lake/uploads/pdf/commit', {
        method: 'POST',
        body: JSON.stringify({
          jobIds: jobs.filter(j => j.status === 'ready').map(j => j.jobId),
          tableName: appendMode ? appendTo : tableName.trim(),
          mode: appendMode ? 'append' : 'create',
          appendTo: appendMode ? appendTo : undefined,
          columns: schema.map(c => ({ sourceName: c.sourceName, targetName: c.targetName, type: c.type, included: c.include })),
        }),
      })
      if (!res.ok) { const { error } = await res.json().catch(() => ({})); throw new Error(error || 'Commit failed') }
      setCommitResult(await res.json())
      setPhase('done')
    } catch (e) {
      setErr(e.message)
      setPhase('review')
    }
  }

  const reset = () => { setPhase('idle'); setJobs([]); setSchema([]); setAllRows([]); setExtraText([]); setTableName(''); setTableNameErr(''); setCommitResult(null); setErr(null); setAppendMode(false); setAppendTo(''); setExistingSeeds([]) }

  const sampleRows = allRows.slice(0, 5)

  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[
        { label: 'Catalog', onClick: onBack },
        { label: 'Uploads', onClick: onBackToUploads },
        { label: 'PDF Documents' },
      ]} />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>PDF Documents</h1>
          <p className={styles.sub}>Upload order forms, purchase orders, and delivery sheets to extract structured tables</p>
        </div>
        <span className={`${styles.layerLabel} ${styles.layerLabel_sky}`}>PDF</span>
      </div>

      {err && <div className={styles.uploadErrBanner}>{err}</div>}

      {phase === 'idle' && (
        <div
          className={styles.pdfDropZone}
          onDrop={e => { e.preventDefault(); const files = Array.from(e.dataTransfer.files).filter(f => f.name.endsWith('.pdf')); if (files.length) startUpload(files) }}
          onDragOver={e => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".pdf" multiple style={{ display: 'none' }}
            onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) startUpload(files) }} />
          <div className={styles.pdfDropIcon}>⬆</div>
          <div className={styles.pdfDropTitle}>Drop PDFs here or click to browse</div>
          <div className={styles.pdfDropSub}>Order forms, purchase orders, delivery sheets — one or many at once</div>
        </div>
      )}

      {(phase === 'uploading' || phase === 'extracting') && (
        <div className={styles.pdfProgressPanel}>
          <div className={styles.pdfProgressTitle}>
            {phase === 'uploading' ? 'Uploading…' : 'Extracting data from your documents…'}
          </div>
          {jobs.map((j, i) => (
            <div key={i} className={styles.pdfProgressItem}>
              <span className={styles.pdfProgressFile}>{j.filename}</span>
              <span className={j.status === 'ready' ? styles.pdfProgressDone : j.status === 'failed' ? styles.pdfProgressFail : styles.pdfProgressPending}>
                {j.status === 'ready' ? '✓ Ready' : j.status === 'failed' ? '✗ Failed' : phase === 'uploading' ? 'Uploading…' : 'Extracting…'}
              </span>
            </div>
          ))}
          {phase === 'extracting' && (
            <div className={styles.pdfProgressNote}>This may take 30–60 seconds per document</div>
          )}
        </div>
      )}

      {phase === 'review' && (
        <>
          {schema.some(c => c.isConflict) && (
            <div className={styles.pdfConflictBanner}>
              <span className={styles.pdfConflictIcon}>⚠</span>
              Some fields had different names across your documents — review the merged column names below
            </div>
          )}
          <div className={styles.monoMuted} style={{ marginBottom: '0.75rem' }}>
            Source document{jobs.length !== 1 ? 's' : ''}:{' '}
            {jobs.filter(j => j.status === 'ready').map((j, i) => (
              <span key={j.jobId}>
                {i > 0 && ', '}
                {j.filename}{' '}
                <button type="button" className={styles.pdfDoneLink} onClick={() => viewSourcePdf(j.jobId)}>View PDF ↗</button>
              </span>
            ))}
          </div>
          <section className={styles.section}>
            <div className={styles.pdfSchemaHeader}>
              <div>
                <h2 className={styles.sectionTitle}>Schema review</h2>
                <p className={styles.sectionSub}>Confirm column names and types, then commit to your data lake.</p>
              </div>
              <div className={styles.pdfCommitRow}>
                {existingSeeds.length > 0 && (
                  <div className={styles.pdfAppendToggle}>
                    <label className={styles.pdfAppendLabel}>
                      <input type="checkbox" checked={appendMode} onChange={e => { setAppendMode(e.target.checked); setTableNameErr('') }} />
                      {' '}Append to existing table
                    </label>
                  </div>
                )}
                <div className={styles.pdfTableNameGroup}>
                  {appendMode ? (
                    <>
                      <label className={styles.pdfTableNameLabel}>Append to</label>
                      <select value={appendTo} onChange={e => { setAppendTo(e.target.value); setTableNameErr('') }}
                        className={`${styles.pdfTableNameInput}${tableNameErr ? ` ${styles.pdfTableNameInputErr}` : ''}`}>
                        <option value="">Select table…</option>
                        {existingSeeds.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </>
                  ) : (
                    <>
                      <label className={styles.pdfTableNameLabel}>New table name</label>
                      <input type="text" value={tableName} onChange={e => { setTableName(e.target.value); setTableNameErr('') }}
                        placeholder="e.g. purchase_orders" className={`${styles.pdfTableNameInput}${tableNameErr ? ` ${styles.pdfTableNameInputErr}` : ''}`} />
                    </>
                  )}
                  {tableNameErr && <span className={styles.pdfTableNameErrMsg}>{tableNameErr}</span>}
                </div>
                <button className={styles.uploadBtn} onClick={handleCommit}>
                  {appendMode ? 'Append to Lake' : 'Commit to Lake'}
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>Include</th>
                    <th>Column Name</th>
                    <th style={{ width: 110 }}>Type</th>
                    <th>Source Fields</th>
                    <th>Sample Values</th>
                  </tr>
                </thead>
                <tbody>
                  {schema.map((col, i) => (
                    <tr key={i} className={`${styles.tableRow} ${!col.include ? styles.pdfRowExcluded : ''}`}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={col.include} onChange={e => updateSchema(i, 'include', e.target.checked)} />
                      </td>
                      <td>
                        <input type="text" value={col.targetName} onChange={e => updateSchema(i, 'targetName', e.target.value)}
                          className={styles.pdfColNameInput} disabled={!col.include} />
                        {col.isConflict && <span className={styles.pdfConflictTag}>merged</span>}
                      </td>
                      <td>
                        <select value={col.type} onChange={e => updateSchema(i, 'type', e.target.value)}
                          className={styles.pdfTypeSelect} disabled={!col.include}>
                          <option value="text">text</option>
                          <option value="integer">integer</option>
                          <option value="decimal">decimal</option>
                          <option value="date">date</option>
                          <option value="boolean">boolean</option>
                        </select>
                      </td>
                      <td className={styles.monoMuted}>
                        {col.isConflict
                          ? (col.aliases || []).map(a => `"${a.name}" (${a.count})`).join(' / ')
                          : col.sourceName}
                      </td>
                      <td className={styles.monoMuted}>{(col.samples || []).slice(0, 3).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {sampleRows.length > 0 && (
              <details className={styles.pdfSampleData}>
                <summary className={styles.pdfSampleSummary}>Preview ({sampleRows.length} of {allRows.length} rows)</summary>
                <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
                  <table className={styles.table}>
                    <thead>
                      <tr>{schema.filter(c => c.include).map((col, i) => <th key={i}>{col.targetName}</th>)}</tr>
                    </thead>
                    <tbody>
                      {sampleRows.map((row, ri) => (
                        <tr key={ri}>
                          {schema.filter(c => c.include).map((col, ci) => (
                            <td key={ci} className={styles.mono}>{row[col.sourceName] || ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {extraText.length > 0 && (
              <details className={styles.pdfSampleData}>
                <summary className={styles.pdfSampleSummary}>
                  Other text found in document ({extraText.length} line{extraText.length !== 1 ? 's' : ''}) — will be saved as <code>{(appendMode ? appendTo : tableName) || '…'}_text</code>
                </summary>
                <p className={styles.sectionSub} style={{ marginTop: '0.5rem' }}>
                  Detected outside the structured table — dimension callouts, notes, and other free-floating text.
                  Not tabular data, so it&rsquo;s kept as its own reference table instead of forced into columns above.
                </p>
                <div style={{ overflowX: 'auto', marginTop: '0.5rem', maxHeight: 260, overflowY: 'auto' }}>
                  <table className={styles.table}>
                    <thead>
                      <tr><th style={{ width: 60 }}>Page</th><th>Text</th></tr>
                    </thead>
                    <tbody>
                      {extraText.map((line, i) => (
                        <tr key={i}>
                          <td className={styles.monoMuted}>{line.page}</td>
                          <td className={styles.mono}>{line.text}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </section>
        </>
      )}

      {phase === 'committing' && (
        <div className={styles.pdfProgressPanel}>
          <div className={styles.pdfProgressTitle}>Creating table in your data lake…</div>
        </div>
      )}

      {phase === 'done' && (
        <div className={styles.uploadOkBanner}>
          Table <strong>{commitResult?.seedName}</strong> created with {commitResult?.rows?.toLocaleString()} rows.
          {commitResult?.textSeedName && (
            <> Also saved <strong>{commitResult.textRows?.toLocaleString()}</strong> lines of non-tabular text to <strong>{commitResult.textSeedName}</strong>.</>
          )}
          {' '}<button className={styles.pdfDoneLink} onClick={onBackToUploads}>View in CSV Files →</button>
        </div>
      )}

      {phase === 'done' && (
        <button className={styles.cancelDeleteBtn} onClick={reset} style={{ marginTop: '1rem' }}>
          Upload another document
        </button>
      )}
    </div>
  )
}

// ── Table detail ───────────────────────────────────────────────────────────────

function TableDetailView({ layer, parentLabel, model, onBack, onBackToLayer }) {
  const { apiFetch } = useAuth()
  const [tableDesc, setTableDesc]           = useState(model.description)
  const [editingTableDesc, setEditTableDesc] = useState(false)
  const [colEdits, setColEdits]             = useState(() =>
    Object.fromEntries((model.columns || []).map(c => [c.name, {
      description:    c.description    || '',
      sensitivity:    c.sensitivity    || '',
      classification: c.classification || '',
    }]))
  )
  const [editingCol, setEditingCol] = useState(null)
  const [draftEdits, setDraftEdits] = useState(null)
  const [saving, setSaving]         = useState(false)
  const [saved, setSaved]           = useState(null)
  const [saveErr, setSaveErr]       = useState(null)
  const [renaming, setRenaming]       = useState(false)
  const [renameVal, setRenameVal]     = useState(model.name)
  const [renameSaving, setRenameSaving] = useState(false)
  const [renameErr, setRenameErr]     = useState(null)
  const [sourceSql, setSourceSql]     = useState(null)
  const [compiledSql, setCompiledSql] = useState(null)
  const [sqlLoading, setSqlLoading]   = useState(false)

  useEffect(() => {
    if (layer !== 'analytics_detail') return
    setSqlLoading(true)
    const layer_ = model.source || ''
    Promise.all([
      apiFetch(`/api/lake/model-source?model=${encodeURIComponent(model.name)}&layer=${encodeURIComponent(layer_)}`)
        .then(r => r.ok ? r.json() : null),
      apiFetch(`/api/lake/model-sql?model=${encodeURIComponent(model.name)}&target=production`)
        .then(r => r.ok ? r.json() : null),
    ])
      .then(([src, compiled]) => {
        setSourceSql(src?.sql || null)
        setCompiledSql(compiled?.sql || null)
      })
      .catch(() => {})
      .finally(() => setSqlLoading(false))
  }, [layer, model.name, model.source, apiFetch])

  const saveRename = async () => {
    const trimmed = renameVal.trim()
    if (!trimmed || trimmed === model.name) { setRenaming(false); return }
    if (!/^[a-z][a-z0-9_]*$/.test(trimmed)) {
      setRenameErr('Name must be lowercase letters, digits, and underscores only')
      return
    }
    setRenameSaving(true)
    setRenameErr(null)
    try {
      const res = await apiFetch('/api/lake/model/rename', {
        method: 'POST',
        body: JSON.stringify({
          old_name: model.name,
          new_name: trimmed,
          model_type: layer === 'analytics_detail' ? 'analytics' : 'prepared',
          layer: model.source,
        }),
      })
      if (!res.ok) {
        const { error } = await res.json().catch(() => ({}))
        throw new Error(error || 'Rename failed')
      }
      // Navigate back to the layer list — the renamed model won't resolve at old name
      onBackToLayer()
    } catch (err) {
      setRenameErr(err.message || 'Rename failed')
    } finally {
      setRenameSaving(false)
    }
  }

  const saveTable = async () => {
    setSaving(true)
    setSaveErr(null)
    try {
      const res = await apiFetch('/api/lake/model/description', {
        method: 'PATCH',
        body: JSON.stringify({
          model_name:  model.name,
          description: tableDesc,
          ...(layer === 'analytics_detail' && { layer: model.source }),
        }),
      })
      if (!res.ok) {
        const { error } = await res.json().catch(() => ({}))
        throw new Error(error || 'Save failed')
      }
      setEditTableDesc(false)
      setSaved('__table__')
      setTimeout(() => setSaved(null), 2000)
    } catch (err) {
      setSaveErr(err.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const startEditCol = (col) => {
    setEditingCol(col.name)
    setDraftEdits({ ...colEdits[col.name] })
    setSaveErr(null)
  }

  const cancelEditCol = () => { setEditingCol(null); setDraftEdits(null) }

  const saveCol = async () => {
    if (!editingCol) return
    setSaving(true)
    setSaveErr(null)
    try {
      const res = await apiFetch('/api/lake/column', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model_name:  model.name,
          column_name: editingCol,
          fields:      draftEdits,
          ...(layer === 'analytics_detail' && { layer: model.source }),
        }),
      })
      if (!res.ok) {
        const { error } = await res.json().catch(() => ({}))
        throw new Error(error || 'Save failed')
      }
      const committed = editingCol
      setColEdits(e => ({ ...e, [committed]: { ...draftEdits } }))
      setEditingCol(null)
      setDraftEdits(null)
      setSaved(committed)
      setTimeout(() => setSaved(null), 2000)
    } catch (err) {
      setSaveErr(err.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const cols = model.columns || []

  return (
    <div className={styles.viewRoot}>
      <Breadcrumb parts={[
        { label: 'Catalog',   onClick: onBack },
        { label: parentLabel, onClick: onBackToLayer },
        { label: model.name },
      ]} />

      <div className={styles.detailHeader}>
        <div className={styles.detailLeft}>
          <div className={styles.detailTop}>
            {renaming ? (
              <div className={styles.renameRow}>
                <input
                  className={styles.renameInput}
                  value={renameVal}
                  onChange={e => setRenameVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(false) }}
                  autoFocus
                />
                <button className={styles.saveBtn} onClick={saveRename} disabled={renameSaving}>
                  {renameSaving ? 'Saving…' : 'Save'}
                </button>
                <button className={styles.cancelBtn} onClick={() => { setRenaming(false); setRenameVal(model.name); setRenameErr(null) }} disabled={renameSaving}>
                  Cancel
                </button>
                {renameErr && <span className={styles.saveErrMsg}>{renameErr}</span>}
              </div>
            ) : (
              <>
                <h1 className={styles.title}>{model.name}</h1>
                <button className={styles.renameBtn} onClick={() => { setRenaming(true); setRenameVal(model.name) }} title="Rename model">
                  Rename
                </button>
              </>
            )}
            {model.source && <span className={styles.sourceTag}>{model.source}</span>}
          </div>
          {editingTableDesc ? (
            <div className={styles.descEdit}>
              <textarea className={styles.descTextarea} rows={3} value={tableDesc}
                onChange={e => setTableDesc(e.target.value)} autoFocus />
              <div className={styles.descActions}>
                <button className={styles.saveBtn} onClick={saveTable} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button className={styles.cancelBtn} onClick={() => setEditTableDesc(false)} disabled={saving}>Cancel</button>
                {saveErr && <span className={styles.saveErrMsg}>{saveErr}</span>}
              </div>
            </div>
          ) : (
            <div className={styles.descRow}>
              <p className={styles.detailDesc}>{tableDesc || <em className={styles.noDesc}>No description</em>}</p>
              <button className={styles.editBtn} onClick={() => setEditTableDesc(true)}>Edit</button>
              {saved === '__table__' && <span className={styles.savedMsg}>✓ Saved</span>}
            </div>
          )}
        </div>
        <div className={styles.detailStats}>
          <Stat label="Rows"      value={model.rows ? Number(model.rows).toLocaleString() : '—'} />
          <div className={styles.statDivider} />
          <Stat label="Refreshed" value={model.lastRefreshed || '—'} small />
        </div>
      </div>

      {cols.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Columns</div>
          <table className={styles.table}>
            <thead>
              <tr><th>Column</th><th>Type</th><th>Description</th><th>Tags</th><th>Tests</th><th></th></tr>
            </thead>
            <tbody>
              {cols.map(col => {
                const edits     = colEdits[col.name] || {}
                const isEditing = editingCol === col.name
                return (
                  <tr key={col.name}>
                    <td><span className={styles.colName}>{col.name}</span></td>
                    <td><span className={styles.typeBadge}>{col.type}</span></td>
                    <td className={styles.descCell}>
                      {isEditing ? (
                        <div className={styles.colEditForm}>
                          {COLUMN_FIELD_SCHEMA.map(field => (
                            <div key={field.key} className={styles.fieldRow}>
                              <span className={styles.fieldLabel}>{field.label}</span>
                              {field.type === 'text' ? (
                                <input
                                  className={styles.descInput}
                                  value={draftEdits?.[field.key] ?? ''}
                                  onChange={e => setDraftEdits(d => ({ ...d, [field.key]: e.target.value }))}
                                  placeholder={field.placeholder || ''}
                                  autoFocus={field.key === 'description'}
                                />
                              ) : (
                                <select
                                  className={styles.fieldSelect}
                                  value={draftEdits?.[field.key] ?? ''}
                                  onChange={e => setDraftEdits(d => ({ ...d, [field.key]: e.target.value }))}
                                >
                                  {field.options.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          ))}
                          {saveErr && <div className={styles.saveErrMsg}>{saveErr}</div>}
                        </div>
                      ) : (
                        <div className={styles.descRow}>
                          <span className={styles.colDesc}>{edits.description || <em className={styles.noDesc}>No description</em>}</span>
                          {saved === col.name && <span className={styles.savedMsg}>✓ Saved</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className={styles.colBadges}>
                        {edits.sensitivity && (
                          <span className={`${styles.sensitivityBadge} ${styles[`sensitivityBadge_${edits.sensitivity}`]}`}>
                            {edits.sensitivity}
                          </span>
                        )}
                        {edits.classification && (
                          <span className={styles.classificationBadge}>{edits.classification}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={styles.testTags}>
                        {(col.tests || []).map(t => <span key={t} className={styles.testTag}>{t}</span>)}
                      </div>
                    </td>
                    <td>
                      {isEditing ? (
                        <div className={styles.descActions}>
                          <button className={styles.saveBtn} onClick={saveCol} disabled={saving}>
                            {saving ? '…' : 'Save'}
                          </button>
                          <button className={styles.cancelBtn} onClick={cancelEditCol} disabled={saving}>✕</button>
                        </div>
                      ) : (
                        <button className={styles.editBtn} onClick={() => startEditCol(col)}>Edit</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}

      {layer === 'analytics_detail' && (
        <section className={styles.section}>
          <div className={styles.sectionLabel}>SQL</div>
          {sqlLoading ? (
            <div className={styles.sqlLoading}>Loading…</div>
          ) : (compiledSql || sourceSql) ? (
            <pre className={styles.sqlBlock}>{compiledSql || sourceSql}</pre>
          ) : (
            <div className={styles.sqlEmpty}>SQL not found.</div>
          )}
        </section>
      )}
    </div>
  )
}

// ── Page shell ─────────────────────────────────────────────────────────────────

export default function LakePage() {
  const { apiFetch } = useAuth()
  const { openChat, dataRefreshKey } = useOutletContext() || {}
  const [view, setView]         = useState('overview')
  const [model, setModel]       = useState(null)
  const [seed, setSeed]         = useState(null)
  const [parentLabel, setParentLabel] = useState(null)
  const [rawTable, setRawTable]       = useState(null)
  const [rawConnector, setRawConnector] = useState(null)
  const [schedule, setSchedule]     = useState('every_6h')
  const [lastDbtRun, setLastDbtRun] = useState(null)
  const [runOpen, setRunOpen]       = useState(false)
  const [activeJob, setActiveJob] = useState(null) // { id, state, errorId }
  const pollRef = useRef(null)

  const [sources, setSources]   = useState([])
  const [prepared, setPrepared] = useState([])
  const [seeds, setSeeds]       = useState([])
  const [analytics, setAnalytics] = useState([])
  const [loading, setLoading]   = useState(true)
  const [connectorMap, setConnectorMap] = useState({}) // provider → connector id
  const [wizardJobs, setWizardJobs] = useState({})     // provider → { jobId, status }
  const [widgetsByModel, setWidgetsByModel] = useState({}) // model_name → widget title

  const fetchLakeData = useCallback(async () => {
    try {
      const [srcRes, prepRes, seedRes, anaRes, connRes, dashRes] = await Promise.all([
        apiFetch('/api/lake/sources'),
        apiFetch('/api/lake/prepared'),
        apiFetch('/api/lake/seeds'),
        apiFetch('/api/lake/analytics'),
        apiFetch('/api/connectors'),
        apiFetch('/api/dashboard'),
      ])
      const [srcData, prepData, seedData, anaData, connData, dashData] = await Promise.all([
        srcRes.json(), prepRes.json(), seedRes.json(), anaRes.json(), connRes.json(), dashRes.json(),
      ])
      setSources(srcData.sources     || [])
      setPrepared(prepData.prepared   || [])
      const newSeeds = seedData.seeds || []
      setSeeds(newSeeds)
      setAnalytics(anaData.analytics  || [])
      setSeed(prev => {
        if (!prev) return prev
        return newSeeds.find(s => s.name === prev.name) ?? prev
      })
      const map = {}
      for (const c of connData.connectors || []) map[c.provider] = c.id
      setConnectorMap(map)
      const wbm = {}
      for (const w of dashData.widgets || []) {
        if (w.model_name) wbm[w.model_name] = w.title
      }
      setWidgetsByModel(wbm)
    } catch (err) {
      console.error('Lake fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [apiFetch])

  const pollWizardJob = useCallback((provider, jobId) => {
    const timer = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/definition-jobs/${jobId}`)
        if (!res.ok) { clearInterval(timer); return }
        const job = await res.json()
        setWizardJobs(prev => ({ ...prev, [provider]: { jobId, status: job.status } }))
        if (job.status === 'success' || job.status === 'failed') {
          clearInterval(timer)
          if (job.status === 'success') {
            fetchLakeData()
            setTimeout(() => setWizardJobs(prev => { const n = { ...prev }; delete n[provider]; return n }), 8000)
          }
        }
      } catch { clearInterval(timer) }
    }, 4000)
  }, [apiFetch, fetchLakeData])

  const handleWizard = useCallback(async (provider) => {
    const connectorId = connectorMap[provider]
    if (!connectorId) return
    try {
      const res = await apiFetch(`/api/connectors/${connectorId}/document`, {
        method: 'POST',
      })
      if (!res.ok) return
      const data = await res.json()
      if (data.defJobId) {
        setWizardJobs(prev => ({ ...prev, [provider]: { jobId: data.defJobId, status: 'queued' } }))
        pollWizardJob(provider, data.defJobId)
      }
    } catch (err) {
      console.error('Wizard trigger error:', err)
    }
  }, [connectorMap, apiFetch, pollWizardJob])

  useEffect(() => { fetchLakeData() }, [fetchLakeData])
  useEffect(() => { if (dataRefreshKey > 0) fetchLakeData() }, [dataRefreshKey])

  useEffect(() => {
    apiFetch('/api/tenant/profile')
      .then(r => r.json())
      .then(profile => {
        if (profile.last_dbt_run_at) setLastDbtRun(profile.last_dbt_run_at)
        if (profile.dbt_schedule) {
          const cronToId = Object.fromEntries(SCHEDULE_OPTIONS.map(s => [s.cron, s.id]))
          const id = cronToId[profile.dbt_schedule]
          if (id) setSchedule(id)
        }
      })
      .catch(() => {})
  }, [apiFetch])

  const handleSaveSchedule = useCallback(async (scheduleId) => {
    const cron = SCHEDULE_OPTIONS.find(s => s.id === scheduleId)?.cron ?? null
    setSchedule(scheduleId)
    await apiFetch('/api/lake/schedule', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dbt_schedule: cron ?? '' }),
    }).catch(err => console.error('Failed to save schedule:', err))
  }, [apiFetch])

  const handleJobStarted = useCallback((jobId) => {
    setActiveJob({ id: jobId, state: 'running' })
  }, [])

  useEffect(() => {
    if (!activeJob || activeJob.state !== 'running') return
    const jobId = activeJob.id
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/lake/refresh/${jobId}`)
        const job = await res.json()
        if (job.status === 'success') {
          clearInterval(pollRef.current)
          setActiveJob({ id: jobId, state: 'done' })
          fetchLakeData()
          setTimeout(() => setActiveJob(null), 6000)
        } else if (job.status === 'failed') {
          clearInterval(pollRef.current)
          setActiveJob(prev => ({ ...prev, state: 'resolving' }))
          try {
            const rRes = await apiFetch(`/api/lake/refresh/${jobId}/resolve`, { method: 'POST' })
            if (rRes.ok) {
              const r = await rRes.json()
              if (r.resolved && r.newJobId) {
                setActiveJob({ id: r.newJobId, state: 'running' })
              } else {
                setActiveJob({ id: jobId, state: 'failed', errorId: r.errorId ?? 'ERR-UNKNOWN' })
              }
            } else {
              setActiveJob({ id: jobId, state: 'failed', errorId: 'ERR-UNKNOWN' })
            }
          } catch {
            setActiveJob({ id: jobId, state: 'failed', errorId: 'ERR-UNKNOWN' })
          }
        }
      } catch { /* network hiccup, keep polling */ }
    }, 2500)
    return () => clearInterval(pollRef.current)
  }, [activeJob?.id, activeJob?.state, apiFetch, fetchLakeData])

  const goTo         = (v)            => { setView(v); setModel(null); setSeed(null); setRawTable(null) }
  const goToModel    = (m, parent, v) => { setModel(m); setParentLabel(parent); setView(v) }
  const goToSeed     = (s)            => { setSeed(s); setView('seed_detail') }
  const goToRawTable = (t, connector) => { setRawTable(t); setRawConnector(connector); setView('raw_detail') }
  const goToTop      = ()             => { setView('overview'); setModel(null); setSeed(null); setRawTable(null) }
  const goToUploads  = ()             => { setView('uploads'); setModel(null); setSeed(null); setRawTable(null) }

  if (loading) {
    return (
      <div className={`${styles.root} anim-fade`}>
        <div className={styles.loadingMsg}>Loading catalog…</div>
      </div>
    )
  }

  return (
    <div className={`${styles.root} anim-fade`}>
      <RefreshStatusBar job={activeJob} onDismiss={() => setActiveJob(null)} openChat={openChat} />
      {view === 'overview' && (
        <OverviewView
          sources={sources}
          prepared={prepared}
          analytics={analytics}
          seeds={seeds}

          onNav={goTo}
          schedule={schedule}
          lastDbtRun={lastDbtRun}
          onSaveSchedule={handleSaveSchedule}
          onRunOpen={() => setRunOpen(true)}
        />
      )}
      {view === 'sources' && (
        <SourcesView sources={sources} onBack={goToTop} onSelectTable={goToRawTable} wizardJobs={wizardJobs} onWizard={handleWizard} />
      )}
      {view === 'raw_detail' && rawTable && (
        <RawTableDetailView
          table={rawTable}
          connectorLabel={rawConnector}
          onBack={goToTop}
          onBackToSources={() => setView('sources')}
        />
      )}
      {view === 'prepared' && (
        <PreparedView
          prepared={prepared}
          onBack={goToTop}
          onSelectModel={(m, connector) => goToModel(m, connector, 'prepared_detail')}
          wizardJobs={wizardJobs}
          onWizard={handleWizard}
        />
      )}
      {view === 'analytics' && (
        <AnalyticsView
          analytics={analytics}
          onBack={goToTop}
          onSelectModel={(m) => goToModel(m, 'Analytics', 'analytics_detail')}
          widgetsByModel={widgetsByModel}
        />
      )}
      {view === 'uploads' && (
        <UploadsView
          seeds={seeds}
          onBack={goToTop}
          onCsvFiles={() => goTo('seeds')}
          onPdfDocs={() => goTo('pdf_documents')}
        />
      )}
      {view === 'seeds' && (
        <SeedsView seeds={seeds} onBack={goToTop} onBackToUploads={goToUploads} openChat={openChat || (() => {})} onRefresh={fetchLakeData} onSelectSeed={goToSeed} />
      )}
      {view === 'pdf_documents' && (
        <DocumentsView onBack={goToTop} onBackToUploads={goToUploads} />
      )}
      {view === 'seed_detail' && seed && (
        <SeedDetailView
          seed={seed}
          onBack={goToTop}
          onBackToUploads={goToUploads}
          onBackToSeeds={() => setView('seeds')}
        />
      )}
      {(view === 'prepared_detail' || view === 'analytics_detail') && (
        <TableDetailView
          layer={view}
          parentLabel={parentLabel}
          model={model}
          onBack={goToTop}
          onBackToLayer={() => setView(view === 'prepared_detail' ? 'prepared' : 'analytics')}
        />
      )}
      {runOpen && <RunModal onClose={() => setRunOpen(false)} onJobStarted={handleJobStarted} />}
    </div>
  )
}
